import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectSessionAdvisor } from '@client/model/selectors';
import { useDocusignTemplateTabs } from './use-docusign-template-tabs';
import {
  buildDefaultSignatoryEmails,
  roleKeyFromRoleName,
  roleKeyLabel,
} from '../utils/signatory-emails';

interface UseSignatoryEmailsParams {
  open: boolean;
  formId: string | null | undefined;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useSignatoryEmails({ open, formId }: UseSignatoryEmailsParams) {
  const advisor = useSelector(selectSessionAdvisor);
  const advisorEmail = advisor?.advisorEmail ?? '';

  const {
    data: templateData,
    isLoading,
    error,
  } = useDocusignTemplateTabs({ formId, enabled: open });

  // Derive the signatory roles from the DocuSign template's signer roles. Only roles
  // that actually have at least one tab can sign — a tab-less signer makes DocuSign
  // reject the envelope as "freeform signing", so we skip those. Unknown role names
  // (not in our docusignRoles map) are surfaced separately.
  const { roleKeys, unmappedRoleNames } = useMemo(() => {
    const keys: DocusignRoleKey[] = [];
    const unmapped: string[] = [];
    const seen = new Set<DocusignRoleKey>();

    for (const signer of templateData?.signers ?? []) {
      const hasTabs = Object.values(signer.tabs ?? {}).some(
        tabList => Array.isArray(tabList) && tabList.length > 0,
      );
      if (!hasTabs) continue;

      const roleKey = roleKeyFromRoleName(signer.roleName);
      if (!roleKey) {
        if (signer.roleName) unmapped.push(signer.roleName);
        continue;
      }
      if (!seen.has(roleKey)) {
        seen.add(roleKey);
        keys.push(roleKey);
      }
    }

    // Keep the preparer first when present; it is always the user's own account.
    if (seen.has('applicationPreparer')) {
      const ordered: DocusignRoleKey[] = [
        'applicationPreparer',
        ...keys.filter(k => k !== 'applicationPreparer'),
      ];
      return { roleKeys: ordered, unmappedRoleNames: unmapped };
    }

    return { roleKeys: keys, unmappedRoleNames: unmapped };
  }, [templateData]);

  const [emails, setEmails] = useState<Record<string, string>>({});

  // Reset to tagged defaults whenever the dialog opens or the resolved roles change.
  useEffect(() => {
    if (open) {
      setEmails(buildDefaultSignatoryEmails(roleKeys, advisorEmail));
    }
  }, [open, roleKeys, advisorEmail]);

  function setEmail(roleKey: DocusignRoleKey, value: string) {
    setEmails(prev => ({ ...prev, [roleKey]: value }));
  }

  const rows = useMemo(
    () =>
      roleKeys.map(roleKey => ({
        roleKey,
        label: roleKeyLabel(roleKey),
        email: emails[roleKey] ?? '',
      })),
    [roleKeys, emails],
  );

  const errors = useMemo<Record<string, string>>(() => {
    const result: Record<string, string> = {};

    for (const roleKey of roleKeys) {
      if (roleKey === 'applicationPreparer') continue;

      const value = (emails[roleKey] ?? '').trim();

      if (!value) {
        result[roleKey] = 'Email is required';
      } else if (!EMAIL_REGEX.test(value)) {
        result[roleKey] = 'Enter a valid email';
      }
    }

    return result;
  }, [roleKeys, emails]);

  const isValid = roleKeys.length > 0 && Object.keys(errors).length === 0 && Boolean(advisorEmail);

  function getRoleEmails(): Record<string, string> {
    const result: Record<string, string> = {};

    for (const roleKey of roleKeys) {
      if (roleKey === 'applicationPreparer') continue;

      const value = (emails[roleKey] ?? '').trim();

      if (value) result[roleKey] = value;
    }
    return result;
  }

  return {
    rows,
    advisorEmail,
    setEmail,
    errors,
    isValid,
    getRoleEmails,
    isLoading,
    error,
    unmappedRoleNames,
  };
}
